import { constants } from '@constants/Constants';
import { getSectorLabel } from '@v2/utils/helper';
import store from '../../../store/store';
import {
  shouldShowSectorFilter,
  shouldShowMarketFilter,
  shouldShowSiteFilter,
} from '@utils/filterVisibilityHelpers';

/**
 * Helper to detect if running as CTX MFE (regardless of selected product/program state)
 */
const isCtxMfeApp = () => {
  try {
    const state = store.getState();
    return (
      state?.app?.isMFE === true &&
      state?.app?.mfeHostConfig?.type === constants?.ctxMfeHostConfig
    );
  } catch (e) {
    return false;
  }
};

/**
 * Adapter utilities to convert legacy filter configurations to PsdFilter props
 * This allows us to reuse existing filter logic with the new PsdFilter components
 */

/**
 * Converts legacy filter options to PsdFilter format
 * Prioritizes integer IDs over string values for proper Redux state
 * @param {Array} options - Legacy options array
 * @param {string} filterKey - The filter key to determine which ID field to use
 * @returns {Array} PsdFilter-compatible options
 */
export const convertOptionsForPsdFilter = (options, filterKey) => {
  if (!Array.isArray(options)) {
    return [];
  }

  return options.map((option) => {
    // Prioritize integer IDs based on filter type
    let value;
    switch (filterKey) {
      case 'program':
        value = option.programId ?? option.id ?? option.value;
        break;
      case 'product':
        value = option.productId ?? option.id ?? option.value;
        break;
      case 'model':
        value = option.id ?? option.value;
        break;
      case 'sector':
        value = option.sectorId ?? option.id ?? option.value;
        break;
      case 'market':
        value = option.marketId ?? option.id ?? option.value;
        break;
      case 'site':
        value = option.plantId ?? option.id ?? option.value;
        break;
      case 'customer':
        value = option.id ?? option.value;
        break;
      default:
        value = option.id ?? option.value;
    }

    return {
      value,
      label:
        option.label ||
        option.programName ||
        option.productName ||
        option.sectorName ||
        option.marketName ||
        option.plantName ||
        option.name,
      disabled: option.disabled || false,
    };
  });
};

/**
 * Creates a static options function for PsdFilter from filter logic context
 * @param {string} filterKey - The filter key (program, product, model, etc.)
 * @param {Object} filterLogic - The filter logic context from useFilterLogic
 * @returns {Function} Static options function for PsdFilter
 */
export const createStaticOptionsFunction = (filterKey, filterLogic) => {
  const optionsMap = {
    program: () =>
      convertOptionsForPsdFilter(filterLogic.programOptions || [], 'program'),
    product: () =>
      convertOptionsForPsdFilter(filterLogic.productOptions || [], 'product'),
    model: () =>
      convertOptionsForPsdFilter(filterLogic.modelOptions || [], 'model'),
    sector: () =>
      convertOptionsForPsdFilter(filterLogic.sectorOptions || [], 'sector'),
    market: () =>
      convertOptionsForPsdFilter(filterLogic.marketOptions || [], 'market'),
    site: () =>
      convertOptionsForPsdFilter(filterLogic.plantOptions || [], 'site'),
    customer: () =>
      convertOptionsForPsdFilter(filterLogic.customerOptions || [], 'customer'),
  };

  return optionsMap[filterKey] || (() => []);
};

/**
 * Determines if a filter should be visible based on legacy filter config
 * @param {string} filterKey - The filter key
 * @param {Object} filterLogic - The filter logic context
 * @returns {boolean} Whether the filter should be visible
 */
export const isFilterVisible = (filterKey, filterLogic) => {
  switch (filterKey) {
    case 'program':
      return !filterLogic.isMFE;

    case 'product': {
      // In CTX MFE host, always show product dropdown so user can switch
      // between CTX products (ProfitToServe, MCVO, etc.).
      if (isCtxMfeApp()) {
        return true;
      }
      return (
        filterLogic.selectedProgram?.showProduct &&
        !filterLogic.isMFE &&
        filterLogic.selectedProgram?.programId !==
          constants?.programs?.Automation &&
        filterLogic.selectedProgram?.programId !== constants?.programs?.GPG
      );
    }

    case 'model':
      return filterLogic.displayModel && filterLogic.selectedProduct?.showModel;

    case 'sector':
      return (
        filterLogic.selectedModel?.showSector &&
        shouldShowSectorFilter(
          filterLogic.selectedModel,
          filterLogic.selectedProgram,
          filterLogic.selectedProduct,
          filterLogic.sectorOptions
        )
      );

    case 'market': {
      if (isCtxMfeApp()) {
        // For CTX MFE bypass the global product-exclusion list (which hides
        // market for ProfitToServe). Show market only when the selected
        // model supports it (showMarket) and market options are loaded.
        return (
          filterLogic.selectedModel?.showMarket &&
          (filterLogic.marketOptions?.length || 0) > 0
        );
      }
      return (
        filterLogic.selectedModel?.showMarket &&
        shouldShowMarketFilter(
          filterLogic.selectedModel,
          filterLogic.selectedProduct,
          filterLogic.marketOptions
        )
      );
    }

    case 'site':
      return shouldShowSiteFilter(
        filterLogic.selectedProgram,
        filterLogic.plantOptions
      );

    case 'customer':
      return (
        filterLogic.selectedModel?.showPlant &&
        filterLogic.selectedProduct?.productId === constants?.products?.R360 &&
        filterLogic.customerOptions.length > 0
      );

    default:
      return true;
  }
};

/**
 * Determines if a filter should be disabled based on legacy filter config
 * @param {string} filterKey - The filter key
 * @param {Object} filterLogic - The filter logic context
 * @returns {boolean} Whether the filter should be disabled
 */
export const isFilterDisabled = (filterKey, filterLogic) => {
  switch (filterKey) {
    case 'market':
    case 'site':
      return (
        filterLogic.selectedSector?.sectorId === constants?.globalIDs?.sectorId
      );

    default:
      return false;
  }
};

/**
 * Gets the label for a filter based on legacy filter config
 * @param {string} filterKey - The filter key
 * @param {Object} filterLogic - The filter logic context
 * @returns {string} The filter label
 */
export const getFilterLabel = (filterKey, filterLogic) => {
  const labelMap = {
    program: constants.filterBarPlaceHolder.program,
    product: constants.filterBarPlaceHolder.product,
    model: constants.filterBarPlaceHolder.model,
    sector: getSectorLabel(filterLogic.selectedProduct?.productId),
    market: constants.filterBarPlaceHolder.market,
    site: constants.filterBarPlaceHolder.site,
    customer: constants.filterBarPlaceHolder.customer,
  };

  return labelMap[filterKey] || '';
};

/**
 * Gets the currently selected value for a filter from legacy context
 * Prioritizes integer IDs to match the format stored in Redux
 * @param {string} filterKey - The filter key
 * @param {Object} filterLogic - The filter logic context
 * @returns {any} The selected value (integer ID)
 */
export const getSelectedValue = (filterKey, filterLogic) => {
  const valueMap = {
    program:
      filterLogic.selectedProgram?.programId ??
      filterLogic.selectedProgram?.id ??
      filterLogic.selectedProgram?.value,
    product:
      filterLogic.selectedProduct?.productId ??
      filterLogic.selectedProduct?.id ??
      filterLogic.selectedProduct?.value,
    model: filterLogic.selectedModel?.id ?? filterLogic.selectedModel?.value,
    sector:
      filterLogic.selectedSector?.sectorId ??
      filterLogic.selectedSector?.id ??
      filterLogic.selectedSector?.value,
    market:
      filterLogic.selectedMarket?.marketId ??
      filterLogic.selectedMarket?.id ??
      filterLogic.selectedMarket?.value,
    site:
      filterLogic.selectedPlant?.plantId ??
      filterLogic.selectedPlant?.id ??
      filterLogic.selectedPlant?.value,
    customer:
      filterLogic.selectedCustomer?.id ?? filterLogic.selectedCustomer?.value,
  };

  return valueMap[filterKey];
};

/**
 * Gets the parent filter dependencies for a filter
 * @param {string} filterKey - The filter key
 * @param {Object} filterLogic - The filter logic context (optional, for dynamic dependencies)
 * @returns {Array<string>} Array of parent filter IDs
 */
export const getParentFilters = (filterKey, filterLogic) => {
  // Base dependencies without considering visibility
  const baseParentMap = {
    program: [],
    product: ['program'],
    model: ['program', 'product'],
    sector: ['program', 'product'],
    market: ['program', 'product'],
    site: ['program', 'product'],
    customer: ['program', 'product'],
  };

  // If no filterLogic provided, return base dependencies
  if (!filterLogic) {
    return baseParentMap[filterKey] || [];
  }

  // Dynamic dependencies based on visibility
  const parents = [...(baseParentMap[filterKey] || [])];

  // Check if model filter is visible
  const isModelVisible =
    filterLogic.displayModel && filterLogic.selectedProduct?.showModel;

  switch (filterKey) {
    case 'sector':
      // Sector depends on model only if model is visible (for Guided Selling)
      if (isModelVisible) {
        parents.push('model');
      }
      break;

    case 'market':
      // Market depends on model only if model is visible
      if (isModelVisible) {
        parents.push('model');
      }
      // Market always depends on sector
      parents.push('sector');
      break;

    case 'site':
      // Site does NOT depend on model (it depends on program directly)
      // It depends on sector for filtering
      parents.push('sector');
      break;

    case 'customer':
      // Customer depends on model only if model is visible
      if (isModelVisible) {
        parents.push('model');
      }
      // Customer depends on sector and market
      parents.push('sector', 'market');
      break;

    default:
      break;
  }

  return parents;
};

/**
 * Determines if a filter should show search functionality
 * @param {string} filterKey - The filter key
 * @returns {boolean} Whether the filter should show search
 */
export const shouldShowSearch = (filterKey) => {
  // Enable search for program and product filters to support type-to-search
  return filterKey === 'program' || filterKey === 'product';
};

/**
 * Filter order for rendering
 */
export const FILTER_ORDER_V2 = [
  'program',
  'product',
  'model',
  'sector',
  'market',
  'site',
  'customer',
];
